import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import ReactDOM from "react-dom";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { buildCheckVoucherPrintHtml } from "../../utils/checkVoucherHtml";
import { buildCostAnalysisReportHtml } from "../../utils/costAnalysisReportHtml";
import Logo from "../../assets/RCS-Logo-White.png";
import { API_BASE_URL, authFetch } from "../../config/api";

const resolveSignatureUrl = (value) => {
  const url = String(value || "").trim();
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_BASE_URL.replace(/\/$/, "")}/${url.replace(/^\/+/, "")}`;
};

const JoLedger = ({
  portalLabel = "Accounting",
  pageTitle = "JO Ledger",
  pageDescription = "Consolidated financial view per Job Order - track all requests, liquidations, cost analyses, and vouchers. Note: Only operational transactions are included in the JO Ledger.",
  scope = "all",
  hideCostAnalysis = false,
  hideVouchers = false,
  hideBilled = false,
  hideGrossProfit = false,
  basePath,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const user = useSelector((state) => state.auth.user);
  const currentUserId = Number(user?.user_id || user?.id || 0);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);

  // Filters
  const [dateFilter, setDateFilter] = useState("week");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [customDateModalOpen, setCustomDateModalOpen] = useState(false);
  const [customDateDraftStart, setCustomDateDraftStart] = useState("");
  const [customDateDraftEnd, setCustomDateDraftEnd] = useState("");
  const [clientNameFilter, setClientNameFilter] = useState("");
  const [jobOrderFilter, setJobOrderFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [scopeFilter, setScopeFilter] = useState(scope || "all");

  // Sorting
  const [sortField, setSortField] = useState("job_order");
  const [sortDirection, setSortDirection] = useState("desc");
  const previousDateFilterRef = useRef("all");

  // Detail modal
  const [selectedJo, setSelectedJo] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [detailError, setDetailError] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [returnModal, setReturnModal] = useState({
    open: false,
    request: null,
    amount: "",
    file: null,
  });
  const [returnLoading, setReturnLoading] = useState(false);
  const returnFileInputRef = useRef(null);
  const [printPreview, setPrintPreview] = useState({
    open: false,
    title: "",
    html: "",
  });
  const [printZoom, setPrintZoom] = useState(1);
  const printFrameRef = useRef(null);
  const [receiptPreview, setReceiptPreview] = useState({
    open: false,
    request: null,
  });

  const isRequestorPortal = location.pathname.startsWith("/requestor");
  const isAdminPortal = location.pathname.startsWith("/admin");
  const isCreatedScope = isRequestorPortal || isAdminPortal ? true : scopeFilter === "created";
  const ledgerBasePath =
    basePath ||
    (location.pathname.startsWith("/requestor/jo-ledger")
      ? "/requestor/jo-ledger"
      : "/accounting/jo-ledger");
  const routeJobOrder = params?.jobOrder
    ? decodeURIComponent(params.jobOrder)
    : "";
  const unliquidatedBasePath = `${ledgerBasePath}/unliquidated`;
  const isUnliquidatedRoute =
    location.pathname.startsWith(unliquidatedBasePath);
  const ledgerListPath = isUnliquidatedRoute
    ? unliquidatedBasePath
    : ledgerBasePath;
  const showCostAnalysis = !hideCostAnalysis && !isCreatedScope;
  const showVouchers = !hideVouchers && !isCreatedScope;
  const showBilled = !hideBilled && !isCreatedScope;
  const showGrossProfit = !hideGrossProfit && !isCreatedScope;

  const formatCurrency = (value) => {
    const num = Number(value || 0);
    return num.toLocaleString("en-PH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const getLiquidationReturnDueAmount = (request) => {
    return getLiquidationFinancials(request).returnDue;
  };

  const getLiquidationReturnPaidAmount = (request) =>
    Number(request?.liquidation?.return_amount || 0);

  const getLiquidationReturnOutstandingAmount = (request) => {
    const due = getLiquidationReturnDueAmount(request);
    const paid = getLiquidationReturnPaidAmount(request);
    return Math.max(due - paid, 0);
  };

  const getLiquidationFinancials = (request) => {
    const report = request?.liquidation_report || request?.liquidation || request || {};
    const actualRows = Array.isArray(report.actual_cost_rows) ? report.actual_cost_rows : [];
    const advanceRows = Array.isArray(report.advance_rows) ? report.advance_rows : [];
    const actualCost = actualRows.length
      ? actualRows.reduce(
          (sum, row) => (row?.is_unliquidated ? sum : sum + Number(row?.amount || 0)),
          0
        )
      : Number(report.total_actual_cost_incurred || 0);
    // No saved advance rows means the liquidation report never captured its
    // own advances breakdown — fall back to the original request's
    // particulars (the cash-advance line items for this same request) rather
    // than trusting total_advances, which is often just a stale/blank 0 in
    // that case.
    const particularsTotal = Array.isArray(request?.particulars)
      ? request.particulars.reduce(
          (sum, particular) =>
            sum + (Number(particular?.total) || Number(particular?.amount) || 0),
          0
        )
      : 0;
    const advances = advanceRows.length
      ? advanceRows.reduce((sum, row) => sum + Number(row?.amount || 0), 0)
      : particularsTotal;
    const settlement = actualCost - advances;

    return {
      actualCost,
      advances,
      reimbursement: settlement > 0 ? settlement : 0,
      returnDue: settlement < 0 ? Math.abs(settlement) : 0,
    };
  };

  const getRowReimbursementTotal = (row) =>
    (row?.liquidations || []).reduce((sum, liquidation) => {
      const value = getLiquidationFinancials(liquidation).reimbursement;
      return value > 0 ? sum + value : sum;
    }, 0);

  const getRowUnliquidatedTotal = (row) =>
    (row?.liquidations || []).reduce((sum, liquidation) => {
      const actualRows =
        liquidation?.liquidation_report?.actual_cost_rows || [];
      const unliquidatedTotal = actualRows.reduce((rowSum, actualRow) => {
        if (!actualRow?.is_unliquidated) return rowSum;
        return rowSum + Number(actualRow.amount || 0);
      }, 0);
      return sum + unliquidatedTotal;
    }, 0);

  const getRowReturnOutstandingTotal = (row) =>
    (row?.liquidations || []).reduce((sum, liquidation) => {
      const value = getLiquidationFinancials(liquidation).returnDue;
      return value > 0 ? sum + value : sum;
    }, 0);

  const getLiquidationReturnStatus = (request) => {
    const due = getLiquidationReturnDueAmount(request);
    const paid = getLiquidationReturnPaidAmount(request);
    const hasReceipt = Boolean(request?.liquidation?.return_receipt_file_path);

    if (due <= 0) return { label: "-", tone: "slate" };
    if (!paid) return { label: "Pending", tone: "red" };
    if (paid < due)
      return { label: hasReceipt ? "Partial" : "Pending", tone: "amber" };
    return { label: "Settled", tone: "green" };
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString("en-PH", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getDateRange = useCallback(
    (filter) => {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      switch (filter) {
        case "today":
          return {
            start: today.toISOString().split("T")[0],
            end: today.toISOString().split("T")[0],
          };
        case "week": {
          const dayOfWeek = today.getDay();
          const startOfWeek = new Date(today);
          startOfWeek.setDate(today.getDate() - dayOfWeek);
          const endOfWeek = new Date(startOfWeek);
          endOfWeek.setDate(startOfWeek.getDate() + 6);
          return {
            start: startOfWeek.toISOString().split("T")[0],
            end: endOfWeek.toISOString().split("T")[0],
          };
        }
        case "month": {
          const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
          const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
          return {
            start: startOfMonth.toISOString().split("T")[0],
            end: endOfMonth.toISOString().split("T")[0],
          };
        }
        case "custom":
          if (customStartDate && customEndDate) {
            return { start: customStartDate, end: customEndDate };
          }
          return null;
        default:
          return null;
      }
    },
    [customStartDate, customEndDate]
  );

  const getActiveDateRangeLabel = useCallback(() => {
    const dateRange = getDateRange(dateFilter);
    if (!dateRange)
      return dateFilter === "custom" ? "Custom Range" : "All Time";
    return `${formatDate(dateRange.start)} - ${formatDate(dateRange.end)}`;
  }, [dateFilter, getDateRange]);

  const loadLedger = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const url = new URL(`${API_BASE_URL}/api/accounting/jo-ledger`);

      if (isCreatedScope) {
        url.searchParams.set("scope", "created");
      }

      const dateRange = getDateRange(dateFilter);
      if (dateRange) {
        url.searchParams.set("startDate", dateRange.start);
        url.searchParams.set("endDate", dateRange.end);
      }
      if (clientNameFilter.trim()) {
        url.searchParams.set("clientName", clientNameFilter.trim());
      }
      if (jobOrderFilter.trim()) {
        url.searchParams.set("jobOrder", jobOrderFilter.trim());
      }
      if (statusFilter !== "all") {
        url.searchParams.set("status", statusFilter);
      }

      const res = await authFetch(url, { credentials: "include" });
      const json = await res.json();

      if (json.success) {
        setData(json.data || []);
        setSummary(json.summary || null);
      } else {
        setError(json.message || "Failed to load JO ledger");
      }
    } catch (err) {
      console.error("Load JO ledger error:", err);
      setError("Failed to connect to server");
    } finally {
      setLoading(false);
    }
  }, [
    dateFilter,
    getDateRange,
    clientNameFilter,
    jobOrderFilter,
    statusFilter,
    isCreatedScope,
  ]);

  const loadJoDetail = useCallback(
    async (jobOrder) => {
      setDetailLoading(true);
      setDetailError("");
      setDetailData(null);
      setActiveTab("overview");

      try {
        const url = new URL(
          `${API_BASE_URL}/api/accounting/jo-ledger/${encodeURIComponent(
            jobOrder
          )}`
        );
        if (isCreatedScope) {
          url.searchParams.set("scope", "created");
        }

        const res = await authFetch(url, { credentials: "include" });
        const json = await res.json();

        if (json.success) {
          const detailRequests = await Promise.all(
            (json.data?.requests || []).map(async (request) => {
              if (!request?.liquidation) return request;
              try {
                const reportUrl = new URL(
                  `${API_BASE_URL}/api/accounting/liquidations/${encodeURIComponent(request.id)}`
                );
                if (isCreatedScope) reportUrl.searchParams.set("scope", "created");
                const reportResponse = await authFetch(reportUrl, { credentials: "include" });
                const reportJson = await reportResponse.json();
                if (!reportResponse.ok || !reportJson.success || !reportJson.data?.liquidation_report) {
                  return request;
                }
                return {
                  ...request,
                  liquidation: reportJson.data.liquidation || request.liquidation,
                  liquidation_report: reportJson.data.liquidation_report,
                };
              } catch {
                return request;
              }
            })
          );
          setDetailData({ ...json.data, requests: detailRequests });
        } else {
          setDetailError(json.message || "Failed to load JO detail");
        }
      } catch (err) {
        console.error("Load JO detail error:", err);
        setDetailError("Failed to connect to server");
      } finally {
        setDetailLoading(false);
      }
    },
    [isCreatedScope]
  );

  useEffect(() => {
    loadLedger();
  }, [loadLedger]);

  useEffect(() => {
    if (routeJobOrder) {
      setSelectedJo(routeJobOrder);
      loadJoDetail(routeJobOrder);
      return;
    }

    setSelectedJo(null);
    setDetailLoading(false);
    setDetailData(null);
    setDetailError("");
    setActiveTab("overview");
  }, [loadJoDetail, routeJobOrder]);

  const handleSearch = () => {
    loadLedger();
  };

  const openCustomDateModal = () => {
    setCustomDateDraftStart(customStartDate);
    setCustomDateDraftEnd(customEndDate);
    setCustomDateModalOpen(true);
  };

  const closeCustomDateModal = () => {
    setCustomDateModalOpen(false);
  };

  const applyCustomDateRange = () => {
    setCustomStartDate(customDateDraftStart);
    setCustomEndDate(customDateDraftEnd);
    setDateFilter("custom");
    setCustomDateModalOpen(false);
  };

  const handleReset = () => {
    setDateFilter("all");
    setCustomStartDate("");
    setCustomEndDate("");
    setCustomDateModalOpen(false);
    setCustomDateDraftStart("");
    setCustomDateDraftEnd("");
    setClientNameFilter("");
    setJobOrderFilter("");
    setStatusFilter("all");
    previousDateFilterRef.current = "all";
    setTimeout(() => {
      loadLedger();
    }, 0);
  };

  const openJoDetail = (jobOrder) => {
    if (!jobOrder) return;
    navigate(`${ledgerListPath}/${encodeURIComponent(jobOrder)}`);
  };

  const closeJoDetail = () => {
    navigate(ledgerListPath);
  };

  const openPrintPreview = (title, html) => {
    setPrintZoom(1);
    setPrintPreview({
      open: true,
      title,
      html,
    });
  };

  const closePrintPreview = () => {
    setPrintPreview({
      open: false,
      title: "",
      html: "",
    });
  };

  const openReturnModal = (request) => {
    const returnAmount = getLiquidationReturnOutstandingAmount(request);
    if (!returnAmount) return;

    setDetailError("");
    setReturnModal({
      open: true,
      request,
      amount: returnAmount.toFixed(2),
      file: null,
    });
  };

  const closeReturnModal = () => {
    setReturnModal({
      open: false,
      request: null,
      amount: "",
      file: null,
    });
  };

  const handleReturnFileChange = (file) => {
    setReturnModal((current) => ({
      ...current,
      file,
    }));
  };

  const triggerReturnFilePicker = () => {
    returnFileInputRef.current?.click();
  };

  const openReceiptPreview = (request) => {
    if (!request?.liquidation?.return_receipt_file_path) return;
    setReceiptPreview({
      open: true,
      request,
    });
  };

  const closeReceiptPreview = () => {
    setReceiptPreview({
      open: false,
      request: null,
    });
  };

  const submitReturnAction = async () => {
    const request = returnModal.request;
    const amount = Number(returnModal.amount || 0);

    if (!request?.id) {
      setDetailError("Return target is missing.");
      return;
    }

    if (!(amount > 0)) {
      setDetailError("Return amount must be greater than zero.");
      return;
    }

    if (!returnModal.file) {
      setDetailError("Upload the return receipt before proceeding.");
      return;
    }

    setReturnLoading(true);
    setDetailError("");

    try {
      const formData = new FormData();
      formData.append("return_amount", String(amount));
      formData.append("file", returnModal.file);

      const res = await authFetch(
        `${API_BASE_URL}/api/accounting/liquidations/${request.id}/return`,
        {
          method: "POST",
          credentials: "include",
          body: formData,
        }
      );
      const json = await res.json();
      if (!json.success) {
        throw new Error(json.message || "Failed to record liquidation return");
      }

      const updatedDetail = json.data || null;
      if (updatedDetail) {
        setDetailData(updatedDetail);
      }

      const updatedRowReturn = Number(
        updatedDetail?.summary?.total_return || 0
      );
      const currentRow = data.find(
        (row) => String(row.job_order || "") === String(request.job_order || "")
      );
      const previousRowReturn = Number(currentRow?.summary?.total_return || 0);

      setData((current) =>
        current.map((row) =>
          String(row.job_order || "") === String(request.job_order || "")
            ? {
                ...row,
                liquidation: updatedDetail?.liquidation || row.liquidation,
                liquidation_report:
                  updatedDetail?.liquidation_report || row.liquidation_report,
                summary: {
                  ...(row.summary || {}),
                  total_return: updatedRowReturn,
                },
              }
            : row
        )
      );

      setSummary((current) =>
        current
          ? {
              ...current,
              totalReturn: Math.max(
                Number(current.totalReturn || 0) -
                  previousRowReturn +
                  updatedRowReturn,
                0
              ),
            }
          : current
      );

      closeReturnModal();
      await loadJoDetail(request.job_order);
      await loadLedger();
    } catch (err) {
      setDetailError(err.message || "Failed to record liquidation return");
    } finally {
      setReturnLoading(false);
    }
  };

  const printPreviewNow = () => {
    const frameWindow = printFrameRef.current?.contentWindow;
    if (frameWindow) {
      frameWindow.focus();
      frameWindow.print();
    }
  };

  const fitPrintFrameHeight = () => {
    const frame = printFrameRef.current;
    if (!frame) return;

    try {
      frame.style.height = "calc(92vh - 120px)";
      frame.style.minHeight = "0";
      frame.style.maxHeight = "calc(92vh - 120px)";
    } catch (err) {
      console.warn("Unable to measure print preview height", err);
    }
  };

  // Print/View functions
  const buildRequestPrintHtml = (request) => {
    // Check if request is USD currency
    const isUsd = request.currency === "USD" && request.exchange_rate > 0;
    const requestExchangeRate = Number(request.exchange_rate) || 0;

    const particularsHtml = (request.particulars || [])
      .map((p) => {
        // p.amount and p.total are stored in original currency (USD if USD request)
        const totalOriginal = Number(p.total || 0);
        // total_amount in request is always PHP
        const totalPhp = isUsd ? totalOriginal * requestExchangeRate : totalOriginal;

        // Always compute tax as 12% of PHP total
        const taxAmountPhp = p.with_tax ? (totalPhp / 1.12 * 0.12) : 0;
        const netAmountPhp = totalPhp - taxAmountPhp;

        // USD equivalents
        const taxAmountUsd = isUsd && requestExchangeRate > 0 ? taxAmountPhp / requestExchangeRate : 0;
        const netAmountUsd = isUsd && requestExchangeRate > 0 ? netAmountPhp / requestExchangeRate : 0;

        // Build unit cost display
        const unitCostDisplay = isUsd
          ? `$${formatCurrency(p.amount)} <span style="color:#555;">₱${formatCurrency(Number(p.amount) * requestExchangeRate)}</span>`
          : `₱${formatCurrency(p.amount)}`;

        // Build currency info line for USD
        const currencyInfoLine = isUsd
          ? `<div style="font-size:11px; color:#555; margin-top:2px;"><strong>Currency:</strong> USD <strong>Conversion Rate:</strong> ${requestExchangeRate.toFixed(4)}</div>`
          : "";

        // Build net amount display
        const netAmountDisplay = p.with_tax
          ? (isUsd
              ? `$${formatCurrency(netAmountUsd)}<br><span style="font-size:11px; color:#555;">₱${formatCurrency(netAmountPhp)}</span>`
              : `₱${formatCurrency(netAmountPhp)}`)
          : "—";

        // Build tax amount display
        const taxAmountDisplay = p.with_tax
          ? (isUsd
              ? `$${formatCurrency(taxAmountUsd)}<br><span style="font-size:11px; color:#555;">₱${formatCurrency(taxAmountPhp)}</span>`
              : `₱${formatCurrency(taxAmountPhp)}`)
          : "—";

        // Build total display
        const totalDisplay = isUsd
          ? `$${formatCurrency(totalOriginal)}<br><span style="font-size:11px; color:#555;">₱${formatCurrency(totalPhp)}</span>`
          : `₱${formatCurrency(totalPhp)}`;

        return `<tr>
          <td style="padding:6px 8px; border:1px solid #000;">
            <div>${p.description || ""}</div>
            <div><strong>Qty:</strong> ${p.quantity || ""} <strong>Unit Cost:</strong> ${unitCostDisplay}</div>
            ${currencyInfoLine}
          </td>
          <td style="padding:6px 8px; border:1px solid #000; text-align:right;">${netAmountDisplay}</td>
          <td style="padding:6px 8px; border:1px solid #000; text-align:right;">${taxAmountDisplay}</td>
          <td style="padding:6px 8px; border:1px solid #000; text-align:right; font-weight:600;">${totalDisplay}</td>
        </tr>`;
      })
      .join("");

    const attachmentsHtml = (request.attachments || [])
      .map(
        (doc) => `
          <tr>
            <td style="padding:8px 10px; border:1px solid #000;">
              <div style="font-weight:600;">${
                doc.file_name || "Attachment"
              }</div>
              <div style="font-size:11px; color:#555; margin-top:2px;">
                ${doc.uploaded_by_name || "Uploaded"}${
          doc.created_at
            ? ` • ${new Date(doc.created_at).toLocaleDateString()}`
            : ""
        }
              </div>
            </td>
            <td style="padding:8px 10px; border:1px solid #000; text-align:right;">
              <a href="${
                doc.file_url || doc.file_path || "#"
              }" target="_blank" rel="noreferrer">Open</a>
            </td>
          </tr>`
      )
      .join("");

    const attachmentPreviewHtml = (request.attachments || [])
      .map((doc) => {
        const fileUrl = doc.file_url || doc.file_path || "";
        const fileType = String(doc.file_type || "").toLowerCase();
        const fileName = doc.file_name || "Attachment";
        const isImage =
          fileType.startsWith("image/") ||
          /\.(png|jpe?g|webp|gif)$/i.test(fileName);
        const isPdf =
          fileType === "application/pdf" || /\.pdf$/i.test(fileName);

        return `
          <div style="border:1px solid #000; margin-top:10px; padding:10px;">
            <div style="display:flex; justify-content:space-between; gap:12px; align-items:flex-start;">
              <div>
                <div style="font-weight:700;">${fileName}</div>
                <div style="font-size:11px; color:#555; margin-top:2px;">
                  ${doc.uploaded_by_name || "Uploaded"}${
          doc.created_at
            ? ` • ${new Date(doc.created_at).toLocaleDateString()}`
            : ""
        }
                </div>
              </div>
              <a href="${
                fileUrl || "#"
              }" target="_blank" rel="noreferrer" style="font-size:12px;">Open</a>
            </div>
            <div style="margin-top:10px;">
              ${
                isImage
                  ? `<img src="${fileUrl}" alt="${fileName}" style="width:100%; max-height:520px; object-fit:contain; border:1px solid #ccc;">`
                  : isPdf
                  ? `<iframe src="${fileUrl}" title="${fileName}" style="width:100%; height:520px; border:1px solid #ccc;"></iframe>`
                  : `<div style="padding:14px; border:1px dashed #777; font-size:12px; color:#333;">Preview not available for this file type.</div>`
              }
            </div>
          </div>`;
      })
      .join("");

    return `
      <html><head><title>Cash Voucher ${request.rfp_no || ""}</title>
      <style>
        *{box-sizing:border-box;} body{font-family:"Times New Roman",serif;color:#000;margin:18px;}
        h1{text-align:center;letter-spacing:2px;margin:0 0 4px;}
        .subhead{text-align:center;font-weight:600;margin:0 0 12px;}
        table{width:100%;border-collapse:collapse;margin-top:8px;}
        th,td{border:1px solid #000;padding:6px 8px;vertical-align:top;}
        th{text-align:left;}
        .tot{margin-top:6px;display:flex;justify-content:flex-end;}
        .tot .box{border:1px solid #000;padding:6px 12px;min-width:220px;}
        .sig-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:16px;margin-top:14px;}
        .sig{text-align:center;min-height:110px;display:flex;flex-direction:column;justify-content:flex-end;gap:6px;}
        .sig img{max-height:52px;object-fit:contain;margin:0 auto;}
        .sig-line{border-top:1px solid #000;height:1px;margin:6px 0;}
        .note{font-size:12px;margin-top:4px;}
      </style></head><body>
        <h1>CASH VOUCHER</h1>
        <div class="subhead">RFP No: ${request.rfp_no || ""}</div>
        <div style="margin-bottom:10px;">
          <div><strong>Date:</strong> ${
            request.created_at
              ? new Date(request.created_at).toLocaleDateString()
              : ""
          }</div>
          <div><strong>Payee:</strong> ${request.payee || ""}</div>
          <div><strong>Job Order:</strong> ${request.job_order || ""}</div>
        </div>
        <table>
          <thead>
            <tr>
              <th style="width:40%">PARTICULARS<br><small>Reference / Description / Quantity / Unit cost</small></th>
              <th style="width:15%; text-align:right;">NET AMOUNT</th>
              <th style="width:15%; text-align:right;">TAX (12%)</th>
              <th style="width:15%; text-align:right;">TOTAL</th>
            </tr>
          </thead>
          <tbody>
            ${particularsHtml}
            <tr><td style="height:80px;"></td><td></td><td></td><td></td></tr>
          </tbody>
        </table>
        <div class="tot"><div class="box">${
          isUsd
            ? `<strong>TOTAL $</strong> ${formatCurrency(request.total_amount / requestExchangeRate)}<br><span style="font-size:12px; color:#555;"><strong>₱</strong> ${formatCurrency(request.total_amount)}</span>`
            : `<strong>TOTAL ₱</strong> ${formatCurrency(request.total_amount)}`
        }</div></div>
        ${
          request.attachments?.length
            ? `
        <div style="margin-top:14px;">
          <div style="font-weight:700;margin-bottom:6px;">ATTACHED DOCUMENTS</div>
          <table>
            <thead>
              <tr>
                <th style="width:80%;">File</th>
                <th style="width:20%; text-align:right;">Action</th>
              </tr>
            </thead>
            <tbody>
              ${attachmentsHtml}
            </tbody>
          </table>
          <div style="margin-top:12px;">
            <div style="font-weight:700; margin-bottom:6px;">ATTACHMENT PREVIEW</div>
            ${attachmentPreviewHtml}
          </div>
        </div>`
            : ""
        }
        <div style="margin-top:10px;">
          <div><strong>Received from:</strong> _______________________</div>
          <div><strong>The amount of PESOS:</strong> __________________</div>
          <div class="note">In full payment of amount described above.</div>
        </div>
        <div class="sig-grid">
          <div class="sig">
            ${
              request.preparer_signature_url
                ? `<img src="${request.preparer_signature_url}" alt="Preparer signature">`
                : "<div style='height:52px'></div>"
            }
            <div class="sig-line"></div>
            <div>Prepared by: ${
              request.preparer_name || "____________________"
            }</div>
          </div>
          <div class="sig">
            ${
              request.approver_signature_url
                ? `<img src="${request.approver_signature_url}" alt="Approver signature">`
                : "<div style='height:52px'></div>"
            }
            <div class="sig-line"></div>
            <div>Approved by (Final): ${
              request.approver_name || "____________________"
            }</div>
          </div>
          <div class="sig">
            ${
              request.receiver_signature_url
                ? `<img src="${request.receiver_signature_url}" alt="Received signature">`
                : "<div style='height:52px'></div>"
            }
            <div class="sig-line"></div>
            <div>Approved/Received by: ${
              request.receiver_name || "____________________"
            }</div>
          </div>
        </div>
      </body></html>
    `;
  };

  const viewRequestPrint = (request) => {
    if (!request) return;
    openPrintPreview(
      `Cash Voucher ${request.rfp_no || ""}`,
      buildRequestPrintHtml(request)
    );
  };

  const buildLiquidationPrintHtml = (request) => {
    if (!request?.liquidation_report) return "";

    const reportData = request.liquidation_report;
    const actualRows = reportData.actual_cost_rows || [];
    const hasSavedAdvanceRows = Boolean(reportData.advance_rows?.length);
    // No saved advance rows means the report never captured its own advances
    // breakdown — fall back to the original request's particulars (the same
    // cash-advance line items) instead of trusting total_advances, which is
    // often just a stale/blank 0 in that case. Keep this in sync with
    // getLiquidationFinancials, which the ledger table uses.
    const advanceRowsValue = hasSavedAdvanceRows
      ? reportData.advance_rows
      : (request.particulars || []).map((particular) => ({
          particulars: particular.description || particular.name || "—",
          or_no: "",
          amount: Number(particular.total) || Number(particular.amount) || 0,
        }));
    const actualCostTotalValue = actualRows.length
      ? actualRows.reduce(
          (sum, row) => (row?.is_unliquidated ? sum : sum + Number(row?.amount || 0)),
          0
        )
      : Number(reportData.total_actual_cost_incurred || 0);
    const advancesTotalValue = advanceRowsValue.reduce(
      (sum, row) => sum + Number(row.amount || 0),
      0
    );
    const settlementValue = actualCostTotalValue - advancesTotalValue;
    const settlementLabel = settlementValue > 0
      ? "REIMBURSEMENT SUBTOTAL"
      : "RETURN SUBTOTAL";
    const settlementAmount = Math.abs(settlementValue);

    const reportHtml = `
      <html>
        <head>
          <title>Liquidation Report ${request.rfp_no || ""}</title>
          <style>
            *{box-sizing:border-box;}
            body{font-family:"Times New Roman",serif;color:#111;margin:0;background:#fff;}
            .page{max-width:1100px;margin:0 auto;padding:22px 28px 32px;}
            .brand{display:flex;align-items:center;justify-content:center;padding:8px 0 16px;}
            .brand img{height:58px;object-fit:contain;}
            h1{text-align:center;margin:0;font-size:28px;letter-spacing:1px;}
            h2{text-align:center;margin:4px 0 0;font-size:18px;font-weight:700;}
            .meta{margin-top:18px;display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px 24px;font-size:13px;}
            .meta .full{grid-column:1 / -1;}
            .section-title{margin-top:18px;background:#1f3b64;color:#fff;padding:8px 12px;font-size:14px;font-weight:700;letter-spacing:.4px;}
            table{width:100%;border-collapse:collapse;margin-top:10px;font-size:13px;}
            th,td{border:1px solid #111;padding:7px 8px;vertical-align:top;}
            th{background:#f3f5f8;text-align:left;}
            .right{text-align:right;}
            .totals{margin-top:8px;display:flex;justify-content:flex-end;}
            .totals .box{min-width:320px;border:1px solid #111;padding:10px 12px;font-weight:700;}
            .signatures{margin-top:40px;display:grid;grid-template-columns:repeat(2,1fr);gap:24px;}
            .sig-box{text-align:center;padding:12px;}
            .sig-label{font-size:12px;color:#555;margin-bottom:8px;}
            .sig-image{height:60px;display:flex;align-items:center;justify-content:center;margin-bottom:4px;}
            .sig-image img{max-height:60px;max-width:150px;object-fit:contain;}
            .sig-name{font-weight:700;font-size:14px;border-top:1px solid #111;padding-top:8px;min-height:24px;}
            .row-flag{display:inline-block;margin-top:4px;padding:2px 8px;border-radius:999px;background:#fff7ed;color:#c2410c;font-size:11px;font-weight:700;letter-spacing:.04em;}
          </style>
        </head>
        <body>
          <div class="page">
            <div class="brand"><img src="${Logo}" alt="RCS Logo"></div>
            <h1>Liquidation Report</h1>
            <h2>BROKERAGE DEPARTMENT LIQUIDATION REPORT</h2>
            <div class="meta">
              <div><strong>Date:</strong> ${
                reportData.report_date_created
                  ? new Date(
                      reportData.report_date_created
                    ).toLocaleDateString()
                  : "—"
              }</div>
              <div><strong>Consignee:</strong> ${
                reportData.consignee || "—"
              }</div>
              <div><strong>JO:</strong> ${
                request.job_order || reportData.job_order || "—"
              }</div>
              <div><strong>Tracking No:</strong> ${
                reportData.tracking_no || "—"
              }</div>
              <div class="full"><strong>Shipment Details:</strong> ${
                reportData.shipment_details || "—"
              }</div>
              <div><strong>Dock:</strong> ${reportData.dock || "—"}</div>
              <div><strong>P.O No:</strong> ${reportData.po_no || "—"}</div>
              <div><strong>Reference No:</strong> ${
                reportData.reference_no || "—"
              }</div>
              <div><strong>Delivery Date:</strong> ${
                reportData.delivery_date || "—"
              }</div>
            </div>
            <div class="section-title">COST INCURRED FOR SHIPMENT</div>
            <table>
              <thead><tr><th>Particulars</th><th>OR No.</th><th class="right">Amount</th></tr></thead>
              <tbody>
                ${actualRows
                  .map(
                    (row) => `
                  <tr>
                    <td>${row.particulars || "—"}</td>
                    <td>${row.or_no || "—"}</td>
                    <td class="right">
                      <div>${formatCurrency(row.amount)}</div>
                      ${
                        row.is_unliquidated
                          ? `<div class="row-flag">Unliquidated Amount</div>`
                          : ""
                      }
                    </td>
                  </tr>`
                  )
                  .join("")}
              </tbody>
            </table>
            <div class="totals"><div class="box">TOTAL ACTUAL COST INCURRED: ₱${formatCurrency(
              actualCostTotalValue
            )}</div></div>
            <div class="section-title">ADVANCES TABLE</div>
            <table>
              <thead><tr><th>Particulars</th><th>OR No.</th><th class="right">Amount</th></tr></thead>
              <tbody>
                ${advanceRowsValue
                  .map(
                    (row) =>
                      `<tr><td>${row.particulars || "—"}</td><td>${
                        row.or_no || "—"
                      }</td><td class="right">${formatCurrency(
                        row.amount
                      )}</td></tr>`
                  )
                  .join("")}
              </tbody>
            </table>
            <div class="totals"><div class="box">TOTAL ADVANCES: ₱${formatCurrency(
              advancesTotalValue
            )}</div></div>
            <div class="totals" style="margin-top:14px;"><div class="box">${settlementLabel}: ₱${formatCurrency(
              Math.abs(settlementAmount)
            )}</div></div>
            <div class="signatures">
              <div class="sig-box">
                <div class="sig-label">Prepared by</div>
                <div class="sig-image">${
                  resolveSignatureUrl(
                    reportData.preparer_signature_url ||
                      request.preparer_signature_url
                  )
                    ? `<img src="${resolveSignatureUrl(
                        reportData.preparer_signature_url ||
                          request.preparer_signature_url
                      )}" alt="Prepared by signature">`
                    : ""
                }</div>
                <div class="sig-name">${
                  reportData.preparer_name || request.preparer_name || "—"
                }</div>
              </div>
              <div class="sig-box">
                <div class="sig-label">Verified by</div>
                <div class="sig-image">${
                  resolveSignatureUrl(reportData.team_leader_approved_signature_url)
                    ? `<img src="${resolveSignatureUrl(
                        reportData.team_leader_approved_signature_url
                      )}" alt="Verified by signature">`
                    : ""
                }</div>
                <div class="sig-name">${
                  reportData.team_leader_approved_name ||
                  reportData.team_leader_name ||
                  request.team_leader_name ||
                  "—"
                }</div>
              </div>
            </div>
          </div>
        </body>
      </html>
    `;
    return reportHtml;
  };

  const viewLiquidationPrint = async (request) => {
    if (!request?.id) return;

    try {
      // The ledger contains a lightweight report snapshot. Fetch the detail
      // record so the preview always has the complete report and line items.
      const url = new URL(
        `${API_BASE_URL}/api/accounting/liquidations/${encodeURIComponent(
          request.id
        )}`
      );
      if (isCreatedScope) url.searchParams.set("scope", "created");

      const res = await authFetch(url, { credentials: "include" });
      const json = await res.json();
      if (!json.success) {
        throw new Error(json.message || "Failed to load liquidation report");
      }

      const reportRequest = {
        ...request,
        ...json.data,
        liquidation: json.data?.liquidation || request.liquidation,
        liquidation_report:
          json.data?.liquidation_report || request.liquidation_report,
      };

      if (!reportRequest.liquidation_report) {
        throw new Error("Liquidation report data was not found");
      }

      openPrintPreview(
        `Liquidation Report ${reportRequest.rfp_no || ""}`,
        buildLiquidationPrintHtml(reportRequest)
      );
    } catch (err) {
      console.error("Load liquidation report for print error:", err);
      setDetailError(err.message || "Failed to load liquidation report");
    }
  };

  const buildCostAnalysisPrintHtml = (request) => {
    if (!request?.cost_analysis_report) return "";

    const reportData = request.cost_analysis_report;
    const billingRows = reportData.billing_charges_rows || [];
    const receiptedRows = reportData.receipted_charges_rows || [];
    const actualRows = reportData.actual_cost_rows || [];
    // Get approved requests for remarks
    const approvedRequests = (detailData?.requests || []).filter((r) => r.status === "approved");

    const html = buildCostAnalysisReportHtml({
      request,
      reportData,
      billingRows,
      receiptedRows,
      actualRows,
      billingSubtotal: reportData.billing_charges_subtotal || 0,
      receiptedSubtotal: reportData.receipted_charges_subtotal || 0,
      grandTotalValue: reportData.grand_total || 0,
      actualCostTotalValue: reportData.actual_cost_total || 0,
      grossProfitValue: reportData.gross_profit || 0,
      connectedRequests: approvedRequests,
    });
    return html;
  };

  const viewCostAnalysisPrint = (request) => {
    if (!request?.cost_analysis_report) return;
    openPrintPreview(
      `Cost Analysis ${request.rfp_no || ""}`,
      buildCostAnalysisPrintHtml(request)
    );
  };

  const viewVoucherPrint = async (voucher) => {
    if (!voucher) return;

    // Fetch voucher lines
    try {
      const token = localStorage.getItem("authToken");
      const linesRes = await authFetch(
        `${API_BASE_URL}/api/accounting/check-vouchers/${voucher.id}/lines`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const linesData = await linesRes.json();
      const lines = linesData.success ? linesData.data : [];

      // Get requests related to this voucher from detailData
      const relatedRequests =
        detailData?.requests?.filter((r) =>
          voucher.request_ids?.includes(r.id)
        ) || [];

      const html = buildCheckVoucherPrintHtml({
        voucher,
        requests: relatedRequests,
        lines,
      });

      openPrintPreview(
        `Check Voucher ${voucher.voucher_no || voucher.check_no || ""}`,
        html
      );
    } catch (err) {
      console.error("Failed to load voucher details", err);
    }
  };

  // Sorting logic
  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      let aVal, bVal;

      if (sortField === "job_order") {
        aVal = a.job_order || "";
        bVal = b.job_order || "";
      } else if (sortField === "client_name") {
        aVal = a.client_name || "";
        bVal = b.client_name || "";
      } else if (sortField.startsWith("summary.")) {
        const field = sortField.replace("summary.", "");
        aVal = Number(a.summary?.[field] || 0);
        bVal = Number(b.summary?.[field] || 0);
      } else {
        aVal = a[sortField] || "";
        bVal = b[sortField] || "";
      }

      if (typeof aVal === "string") aVal = aVal.toLowerCase();
      if (typeof bVal === "string") bVal = bVal.toLowerCase();

      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortField, sortDirection]);

  const displayReimbursementTotal = useMemo(
    () => data.reduce((sum, row) => sum + getRowReimbursementTotal(row), 0),
    [data]
  );

  const displayReturnTotal = useMemo(
    () => data.reduce((sum, row) => sum + getRowReturnOutstandingTotal(row), 0),
    [data]
  );

  const displayUnliquidatedTotal = useMemo(
    () =>
      Number(summary?.totalUnliquidated || 0) ||
      data.reduce(
        (sum, row) =>
          sum +
          Number(
            row.summary?.total_unliquidated || getRowUnliquidatedTotal(row) || 0
          ),
        0
      ),
    [data, summary?.totalUnliquidated]
  );

  const detailLiquidationSummary = useMemo(() => {
    const requests = detailData?.requests || [];
    return requests.reduce(
      (totals, request) => {
        if (!request?.liquidation) return totals;
        const financials = getLiquidationFinancials(request);
        totals.totalLiquidated += financials.actualCost;
        totals.totalAdvances += financials.advances;
        totals.totalReimbursement += financials.reimbursement;
        totals.totalReturn += financials.returnDue;
        return totals;
      },
      { totalLiquidated: 0, totalAdvances: 0, totalReimbursement: 0, totalReturn: 0 }
    );
  }, [detailData]);

  const filteredData = useMemo(() => {
    let rows = sortedData;

    if (isCreatedScope && currentUserId) {
      rows = rows.filter((row) =>
        (row.requests || []).some(
          (request) =>
            String(request.preparer_user_id || "") ===
            String(currentUserId || "")
        )
      );
    }

    if (isUnliquidatedRoute) {
      rows = rows.filter(
        (row) =>
          Number(
            row.summary?.total_unliquidated || getRowUnliquidatedTotal(row) || 0
          ) > 0
      );
    }

    return rows;
  }, [currentUserId, isCreatedScope, isUnliquidatedRoute, sortedData]);

  const displaySummary = useMemo(() => {
    if (!summary) return null;

    if (!isCreatedScope) {
      return summary;
    }

    return {
      totalJos: filteredData.length,
      totalRequested: filteredData.reduce(
        (sum, row) => sum + Number(row.summary?.total_requested || 0),
        0
      ),
      totalApproved: filteredData.reduce(
        (sum, row) => sum + Number(row.summary?.total_approved || 0),
        0
      ),
      totalLiquidated: filteredData.reduce(
        (sum, row) => sum + Number(row.summary?.total_liquidated || 0),
        0
      ),
      totalBilled: 0,
      totalCollected: 0,
      totalFloating: filteredData.reduce(
        (sum, row) => sum + Number(row.summary?.floating_amount || 0),
        0
      ),
      totalReimbursementReturn: filteredData.reduce(
        (sum, row) => sum + getRowReturnOutstandingTotal(row),
        0
      ),
      totalGrossProfit: 0,
      totalUnliquidated: filteredData.reduce(
        (sum, row) =>
          sum +
          Number(
            row.summary?.total_unliquidated || getRowUnliquidatedTotal(row) || 0
          ),
        0
      ),
    };
  }, [filteredData, isCreatedScope, summary]);

  const mainTableColumnSpan = isCreatedScope ? 9 : 12;

  const detailUnliquidatedTotal = useMemo(
    () =>
      (detailData?.requests || []).reduce((sum, request) => {
        const rows = request?.liquidation_report?.actual_cost_rows || [];
        return (
          sum +
          rows.reduce((rowSum, actualRow) => {
            if (!actualRow?.is_unliquidated) return rowSum;
            return rowSum + Number(actualRow.amount || 0);
          }, 0)
        );
      }, 0),
    [detailData]
  );

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return "fa-sort";
    return sortDirection === "asc" ? "fa-sort-up" : "fa-sort-down";
  };

  // Export to Excel
  const exportToExcel = () => {
    const exportData = filteredData.map((row) => ({
      "JOB ORDER": row.job_order || "",
      CLIENT: row.client_name || "",
      REQUESTS: row.summary?.request_count || 0,
      LIQUIDATIONS: row.summary?.liquidation_count || 0,
      "TOTAL REQUESTED": Number(row.summary?.total_requested || 0),
      "TOTAL APPROVED": Number(row.summary?.total_approved || 0),
      "TOTAL LIQUIDATED": Number(row.summary?.total_liquidated || 0),
      REIMBURSEMENT: getRowReimbursementTotal(row),
      RETURN: getRowReturnOutstandingTotal(row),
      ...(isCreatedScope
        ? {
            UNLIQUIDATED: Number(
              row.summary?.total_unliquidated ||
                getRowUnliquidatedTotal(row) ||
                0
            ),
          }
        : {
            "COST ANALYSES": row.summary?.cost_analysis_count || 0,
            VOUCHERS: row.summary?.voucher_count || 0,
            "TOTAL BILLED": Number(row.summary?.total_billed || 0),
            "TOTAL COLLECTED": Number(row.summary?.total_collected || 0),
            "GROSS PROFIT": Number(row.summary?.gross_profit || 0),
            "VOUCHER TOTAL": Number(row.summary?.voucher_total || 0),
          }),
    }));

    // Add totals row
    if (exportData.length) {
      const totalsSource = filteredData;
      const totals = totalsSource.reduce(
        (acc, row) => {
          acc.totalRequested += Number(row.summary?.total_requested || 0);
          acc.totalApproved += Number(row.summary?.total_approved || 0);
          acc.totalLiquidated += Number(row.summary?.total_liquidated || 0);
          acc.totalReimbursement += getRowReimbursementTotal(row);
          acc.totalReturn += getRowReturnOutstandingTotal(row);
          if (!isCreatedScope) {
            acc.totalBilled += Number(row.summary?.total_billed || 0);
            acc.totalCollected += Number(row.summary?.total_collected || 0);
            acc.totalGrossProfit += Number(row.summary?.gross_profit || 0);
          } else {
            acc.totalUnliquidated += Number(
              row.summary?.total_unliquidated ||
                getRowUnliquidatedTotal(row) ||
                0
            );
          }
          return acc;
        },
        {
          totalRequested: 0,
          totalApproved: 0,
          totalLiquidated: 0,
          totalBilled: 0,
          totalCollected: 0,
          totalReimbursement: 0,
          totalReturn: 0,
          totalGrossProfit: 0,
          totalUnliquidated: 0,
        }
      );

      exportData.push(
        isCreatedScope
          ? {
              "JOB ORDER": "TOTALS",
              CLIENT: "",
              REQUESTS: "",
              LIQUIDATIONS: "",
              "TOTAL REQUESTED": totals.totalRequested || 0,
              "TOTAL APPROVED": totals.totalApproved || 0,
              "TOTAL LIQUIDATED": totals.totalLiquidated || 0,
              REIMBURSEMENT: totals.totalReimbursement || 0,
              RETURN: totals.totalReturn || 0,
              UNLIQUIDATED: totals.totalUnliquidated || 0,
            }
          : {
              "JOB ORDER": "TOTALS",
              CLIENT: "",
              REQUESTS: "",
              LIQUIDATIONS: "",
              "COST ANALYSES": "",
              VOUCHERS: "",
              "TOTAL REQUESTED": totals.totalRequested || 0,
              "TOTAL APPROVED": totals.totalApproved || 0,
              "TOTAL LIQUIDATED": totals.totalLiquidated || 0,
              "TOTAL BILLED": totals.totalBilled || 0,
              "TOTAL COLLECTED": totals.totalCollected || 0,
              REIMBURSEMENT: totals.totalReimbursement || 0,
              RETURN: totals.totalReturn || 0,
              "GROSS PROFIT": totals.totalGrossProfit || 0,
              "VOUCHER TOTAL": "",
            }
      );
    }

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "JO Ledger");

    // Generate filename
    let filename = "jo_ledger_";
    if (dateFilter === "today") {
      filename += `today_${new Date().toISOString().split("T")[0]}`;
    } else if (dateFilter === "week") {
      filename += `week_${new Date().toISOString().split("T")[0]}`;
    } else if (dateFilter === "month") {
      const now = new Date();
      filename += `${now.getFullYear()}_${String(now.getMonth() + 1).padStart(
        2,
        "0"
      )}`;
    } else if (dateFilter === "custom" && customStartDate && customEndDate) {
      filename += `${customStartDate}_to_${customEndDate}`;
    } else {
      filename += "all";
    }
    filename += ".xlsx";

    XLSX.writeFile(wb, filename);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      approved: {
        bg: "bg-green-100",
        text: "text-green-700",
        label: "Approved",
      },
      pending: { bg: "bg-amber-100", text: "text-amber-700", label: "Pending" },
      submitted: {
        bg: "bg-blue-100",
        text: "text-blue-700",
        label: "Submitted",
      },
      verified: {
        bg: "bg-indigo-100",
        text: "text-indigo-700",
        label: "Verified",
      },
      rejected: { bg: "bg-red-100", text: "text-red-700", label: "Rejected" },
      returned: {
        bg: "bg-orange-100",
        text: "text-orange-700",
        label: "Returned",
      },
      draft: { bg: "bg-gray-100", text: "text-gray-700", label: "Draft" },
      journal_created: {
        bg: "bg-emerald-100",
        text: "text-emerald-700",
        label: "Journal Created",
      },
      completed: {
        bg: "bg-teal-100",
        text: "text-teal-700",
        label: "Completed",
      },
    };

    const config = statusConfig[status?.toLowerCase()] || {
      bg: "bg-gray-100",
      text: "text-gray-700",
      label: status || "-",
    };

    return (
      <span
        className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
      >
        {config.label}
      </span>
    );
  };

  const tabs = [
    { key: "overview", label: "Overview", icon: "fa-chart-pie" },
    { key: "requests", label: "Requests", icon: "fa-file-lines" },
    { key: "liquidations", label: "Liquidations", icon: "fa-receipt" },
    ...(showCostAnalysis
      ? [
          {
            key: "cost_analyses",
            label: "Cost Analysis",
            icon: "fa-calculator",
          },
        ]
      : []),
    ...(showVouchers
      ? [{ key: "vouchers", label: "Vouchers", icon: "fa-file-invoice" }]
      : []),
  ];

  // Detail Modal Content
  const DetailModal = () => {
    if (!selectedJo) return null;

    return ReactDOM.createPortal(
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/60 px-4 py-6">
        <div
          className="absolute inset-0"
          role="presentation"
          onClick={closeJoDetail}
        />
        <div className="relative z-10 flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-5">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                JO Ledger Detail
              </p>
              <h3 className="mt-1 text-xl font-bold text-slate-900">
                {selectedJo}
              </h3>
              <p className="text-sm text-slate-500">
                {detailData?.client_name || "-"} | All transactions for this Job
                Order
              </p>
            </div>
            <button
              type="button"
              onClick={closeJoDetail}
              className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
            >
              <i className="fa-solid fa-xmark text-lg" />
            </button>
          </div>

          {/* Tabs */}
          <div className="border-b border-slate-100 px-6">
            <div className="flex gap-1 overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                    activeTab === tab.key
                      ? "border-blue text-blue"
                      : "border-transparent text-slate-500 hover:text-slate-700"
                  }`}
                >
                  <i className={`fa-solid ${tab.icon}`} />
                  {tab.label}
                  {tab.key !== "overview" && detailData && (
                    <span
                      className={`ml-1 px-1.5 py-0.5 rounded text-xs ${
                        activeTab === tab.key ? "bg-blue/10" : "bg-slate-100"
                      }`}
                    >
                      {tab.key === "requests" && detailData.requests?.length}
                      {tab.key === "liquidations" &&
                        detailData.requests?.filter((r) => r.liquidation)
                          .length}
                      {tab.key === "cost_analyses" &&
                        detailData.requests?.filter((r) => r.cost_analysis)
                          .length}
                      {tab.key === "vouchers" &&
                        detailData.check_vouchers?.length}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
            {detailLoading ? (
              <div className="flex min-h-[300px] items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue/20 border-t-blue" />
                  <p className="text-sm text-slate-500">
                    Loading JO details...
                  </p>
                </div>
              </div>
            ) : detailError ? (
              <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
                {detailError}
              </div>
            ) : detailData ? (
              <>
                {/* Overview Tab */}
                {activeTab === "overview" && (
                  <div className="space-y-6">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                      <div className="min-h-[96px] rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                        <p className="mb-1 text-xs text-grey">
                          Total Requested
                        </p>
                        <p className="text-xl font-bold text-blue">
                          {formatCurrency(detailData.summary?.total_requested)}
                        </p>
                      </div>
                      <div className="min-h-[96px] rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                        <p className="mb-1 text-xs text-grey">Total Approved</p>
                        <p className="text-xl font-bold text-green-600">
                          {formatCurrency(detailData.summary?.total_approved)}
                        </p>
                      </div>
                      <div className="min-h-[96px] rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                        <p className="mb-1 text-xs text-grey">
                          Total Liquidated
                        </p>
                        <p className="text-xl font-bold text-indigo-600">
                          {formatCurrency(detailLiquidationSummary.totalLiquidated)}
                        </p>
                      </div>
                      <div className="min-h-[96px] rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                        <p className="mb-1 text-xs text-grey">Total Advances</p>
                        <p className="text-xl font-bold text-blue">
                          {formatCurrency(detailLiquidationSummary.totalAdvances)}
                        </p>
                      </div>
                      <div
                        className={`min-h-[96px] rounded-2xl p-4 shadow-sm border ${
                          detailData.summary?.floating_amount > 0
                            ? "border-amber-300 bg-amber-50"
                            : "border-gray-100 bg-white"
                        }`}
                      >
                        <p className="mb-1 text-xs text-grey">
                          Floating Amount
                        </p>
                        <p
                          className={`text-xl font-bold ${
                            detailData.summary?.floating_amount > 0
                              ? "text-amber-600"
                              : "text-green-600"
                          }`}
                        >
                          {formatCurrency(detailData.summary?.floating_amount)}
                        </p>
                      </div>
                      {!isCreatedScope && (
                        <div className="min-h-[96px] rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                          <p className="mb-1 text-xs text-grey">Total Billed</p>
                          <p className="text-xl font-bold text-blue">
                            {formatCurrency(detailData.summary?.total_billed)}
                          </p>
                        </div>
                      )}
                      {!isCreatedScope && (
                        <div className="min-h-[96px] rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                          <p className="mb-1 text-xs text-grey">
                            Total Collected
                          </p>
                          <p className="text-xl font-bold text-green-600">
                            {formatCurrency(
                              detailData.summary?.total_collected
                            )}
                          </p>
                        </div>
                      )}
                      <div
                        className={`min-h-[96px] rounded-2xl p-4 shadow-sm border ${
                          detailLiquidationSummary.totalReimbursement > 0
                            ? "bg-amber-50 border-amber-200"
                            : "bg-white border-gray-100"
                        }`}
                      >
                        <p className="mb-1 text-xs text-grey">Reimbursement</p>
                        <p
                          className={`text-xl font-bold ${
                            detailLiquidationSummary.totalReimbursement > 0
                              ? "text-amber-600"
                              : "text-slate-400"
                          }`}
                        >
                          {formatCurrency(
                            detailLiquidationSummary.totalReimbursement
                          )}
                        </p>
                        {detailLiquidationSummary.totalReimbursement > 0 && (
                          <p className="mt-2 text-xs italic text-amber-600">
                            Subject for new request
                          </p>
                        )}
                      </div>
                      <div
                        className={`min-h-[96px] rounded-2xl p-4 shadow-sm border ${
                          Number(detailData.summary?.total_unliquidated || 0) >
                            0 ||
                          detailUnliquidatedTotal > 0 ||
                          displayUnliquidatedTotal > 0
                            ? "bg-amber-50 border-amber-200"
                            : "bg-white border-gray-100"
                        }`}
                      >
                        <p className="mb-1 text-xs text-grey">
                          Unliquidated Amount
                        </p>
                        <p
                          className={`text-xl font-bold ${
                            Number(
                              detailData.summary?.total_unliquidated || 0
                            ) > 0 ||
                            detailUnliquidatedTotal > 0 ||
                            displayUnliquidatedTotal > 0
                              ? "text-amber-600"
                              : "text-slate-400"
                          }`}
                        >
                          {formatCurrency(
                            Number(
                              detailData.summary?.total_unliquidated ||
                                detailUnliquidatedTotal ||
                                displayUnliquidatedTotal ||
                                0
                            )
                          )}
                        </p>
                        {(Number(detailData.summary?.total_unliquidated || 0) >
                          0 ||
                          detailUnliquidatedTotal > 0 ||
                          displayUnliquidatedTotal > 0) && (
                          <p className="mt-2 text-xs italic text-amber-600">
                            Excluded from liquidation total
                          </p>
                        )}
                      </div>
                      <div
                        className={`min-h-[96px] rounded-2xl p-4 shadow-sm border ${
                          detailLiquidationSummary.totalReturn > 0
                            ? "bg-green-50 border-green-200"
                            : "bg-white border-gray-100"
                        }`}
                      >
                        <p className="mb-1 text-xs text-grey">
                          Return Subtotal
                        </p>
                        <p
                          className={`text-xl font-bold ${
                            detailData.requests?.some(
                              (req) =>
                                getLiquidationReturnOutstandingAmount(req) > 0
                            )
                              ? "text-green-600"
                              : "text-slate-400"
                          }`}
                        >
                          {formatCurrency(
                            detailLiquidationSummary.totalReturn
                          )}
                        </p>
                        {detailData.requests?.some(
                          (req) =>
                            getLiquidationReturnOutstandingAmount(req) > 0
                        ) && (
                          <p className="mt-2 text-xs text-green-600 italic">
                            {detailData.requests?.some(
                              (req) =>
                                getLiquidationReturnStatus(req).label ===
                                "Partial"
                            )
                              ? "Partial return"
                              : detailData.requests?.some(
                                  (req) =>
                                    getLiquidationReturnStatus(req).label ===
                                    "Settled"
                                )
                              ? "Settled via return receipt"
                              : "Subject for return"}
                          </p>
                        )}
                      </div>
                      {!isCreatedScope && (
                        <div className="min-h-[96px] rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                          <p className="mb-1 text-xs text-grey">Gross Profit</p>
                          <p
                            className={`text-xl font-bold ${
                              detailData.summary?.gross_profit >= 0
                                ? "text-green-600"
                                : "text-red"
                            }`}
                          >
                            {formatCurrency(detailData.summary?.gross_profit)}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Transaction Counts */}
                    <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                      <h4 className="font-semibold text-slate-900 mb-3">
                        Transaction Summary
                      </h4>
                      <div
                        className={`grid gap-4 text-center ${
                          isCreatedScope
                            ? "grid-cols-2"
                            : "grid-cols-2 md:grid-cols-4"
                        }`}
                      >
                        <div>
                          <p className="text-2xl font-bold text-blue">
                            {detailData.summary?.request_count || 0}
                          </p>
                          <p className="text-xs text-slate-500">Requests</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-indigo-600">
                            {detailData.summary?.liquidation_count || 0}
                          </p>
                          <p className="text-xs text-slate-500">Liquidations</p>
                        </div>
                        {!isCreatedScope && (
                          <div>
                            <p className="text-2xl font-bold text-purple-600">
                              {detailData.summary?.cost_analysis_count || 0}
                            </p>
                            <p className="text-xs text-slate-500">
                              Cost Analyses
                            </p>
                          </div>
                        )}
                        {!isCreatedScope && (
                          <div>
                            <p className="text-2xl font-bold text-teal-600">
                              {detailData.summary?.voucher_count || 0}
                            </p>
                            <p className="text-xs text-slate-500">Vouchers</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Requests Tab */}
                {activeTab === "requests" && (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[1080px] table-fixed text-sm">
                        <colgroup>
                          <col className="w-[18%]" />
                          <col className="w-[12%]" />
                          <col className="w-[13%]" />
                          <col className="w-[13%]" />
                          <col className="w-[15%]" />
                          <col className="w-[15%]" />
                          <col className="w-[14%]" />
                        </colgroup>
                        <thead>
                          <tr className="bg-slate-50 border-b border-slate-200">
                            <th className="px-4 py-3 text-left font-semibold text-slate-600">
                              RFP No
                            </th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-600">
                              Payee
                            </th>
                            <th className="px-4 py-3 text-right font-semibold text-slate-600">
                              Amount
                            </th>
                            <th className="px-4 py-3 text-center font-semibold text-slate-600">
                              Status
                            </th>
                            <th className="px-4 py-3 text-center font-semibold text-slate-600">
                              For Liquidation
                            </th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-600">
                              Attachments
                            </th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-600">
                              Created
                            </th>
                            <th className="px-4 py-3 text-center font-semibold text-slate-600">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {detailData.requests?.length ? (
                            detailData.requests.map((req, idx) => (
                              <tr
                                key={req.id}
                                className={`border-b border-slate-100 ${
                                  idx % 2 === 0 ? "" : "bg-slate-50/50"
                                }`}
                              >
                                <td className="px-4 py-3 font-medium">
                                  {req.rfp_no || "-"}
                                </td>
                                <td className="px-4 py-3">
                                  {req.payee || "-"}
                                </td>
                                <td className="px-4 py-3 text-right font-medium">
                                  {formatCurrency(req.total_amount)}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  {getStatusBadge(req.status)}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  {req.for_liquidation ? (
                                    <span className="text-green-600">
                                      <i className="fa-solid fa-check" />
                                    </span>
                                  ) : (
                                    <span className="text-slate-300">
                                      <i className="fa-solid fa-minus" />
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-3 align-top">
                                  {req.attachments?.length ? (
                                    <div className="space-y-2">
                                      {req.attachments.map((doc) => (
                                        <a
                                          key={doc.id}
                                          href={
                                            doc.file_url || doc.file_path || "#"
                                          }
                                          target="_blank"
                                          rel="noreferrer"
                                          className="flex flex-col rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700 hover:border-blue-300 hover:bg-blue-50 transition"
                                        >
                                          <span className="font-medium text-slate-900 break-all">
                                            {doc.file_name || "Attachment"}
                                          </span>
                                          <span className="text-[11px] text-slate-500 mt-0.5">
                                            {doc.uploaded_by_name || "Uploaded"}
                                            {doc.created_at
                                              ? ` • ${formatDate(
                                                  doc.created_at
                                                )}`
                                              : ""}
                                          </span>
                                        </a>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="text-slate-400">-</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-slate-500">
                                  {formatDate(req.created_at)}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <button
                                    onClick={() => viewRequestPrint(req)}
                                    className="px-2 py-1 text-xs font-medium text-blue hover:bg-blue/10 rounded transition"
                                    title="View/Print"
                                  >
                                    <i className="fa-solid fa-eye mr-1" />
                                    View
                                  </button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td
                                colSpan={8}
                                className="px-4 py-8 text-center text-slate-500"
                              >
                                No requests found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Liquidations Tab */}
                {activeTab === "liquidations" && (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-50 border-b border-slate-200">
                            <th className="px-4 py-3 text-left font-semibold text-slate-600">
                              RFP No
                            </th>
                            <th className="px-4 py-3 text-center font-semibold text-slate-600">
                              Status
                            </th>
                            <th className="px-4 py-3 text-right font-semibold text-slate-600">
                              Actual Cost
                            </th>
                            <th className="px-4 py-3 text-right font-semibold text-slate-600">
                              Advances
                            </th>
                            <th className="px-4 py-3 text-right font-semibold text-slate-600">
                              Reimbursement
                            </th>
                            <th className="px-4 py-3 text-right font-semibold text-slate-600">
                              Return
                            </th>
                            <th className="px-4 py-3 text-center font-semibold text-slate-600">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {detailData.requests?.filter((r) => r.liquidation)
                            .length ? (
                            detailData.requests
                              .filter((r) => r.liquidation)
                              .map((req, idx) => {
                                const liquidationFinancials =
                                  getLiquidationFinancials(req);
                                const returnDueAmount =
                                  liquidationFinancials.returnDue;
                                const returnPaidAmount =
                                  getLiquidationReturnPaidAmount(req);
                                const returnOutstanding =
                                  getLiquidationReturnOutstandingAmount(req);
                                const returnStatus =
                                  getLiquidationReturnStatus(req);
                                const reimbursement =
                                  liquidationFinancials.reimbursement;
                                return (
                                  <tr
                                    key={req.liquidation.id}
                                    className={`border-b border-slate-100 ${
                                      idx % 2 === 0 ? "" : "bg-slate-50/50"
                                    }`}
                                  >
                                    <td className="px-4 py-3 font-medium">
                                      {req.rfp_no || "-"}
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                      {getStatusBadge(req.liquidation.status)}
                                    </td>
                                    <td className="px-4 py-3 text-right font-medium">
                                      {formatCurrency(
                                        liquidationFinancials.actualCost
                                      )}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                      {formatCurrency(
                                        liquidationFinancials.advances
                                      )}
                                    </td>
                                    <td className="px-4 py-3 text-right font-medium text-green-600">
                                      {reimbursement > 0
                                        ? formatCurrency(reimbursement)
                                        : "-"}
                                    </td>
                                    <td
                                      className={`px-4 py-3 text-right font-medium ${
                                        returnStatus.tone === "green"
                                          ? "text-green-600"
                                          : returnStatus.tone === "amber"
                                          ? "text-amber-600"
                                          : "text-red-600"
                                      }`}
                                    >
                                      {formatCurrency(returnDueAmount)}
                                      <div
                                        className={`mt-1 text-[11px] font-semibold ${
                                          returnStatus.tone === "green"
                                            ? "text-green-600"
                                            : returnStatus.tone === "amber"
                                            ? "text-amber-600"
                                            : "text-red-600"
                                        }`}
                                      >
                                        {returnStatus.label}
                                        {returnStatus.label === "Partial" && (
                                          <span className="ml-1 font-normal">
                                            ({formatCurrency(returnPaidAmount)}{" "}
                                            paid)
                                          </span>
                                        )}
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-center align-top">
                                      <div className="flex items-center justify-center gap-1 whitespace-nowrap">
                                        <button
                                          onClick={() =>
                                            viewLiquidationPrint(req)
                                          }
                                          className="px-2 py-1 text-xs font-medium text-blue hover:bg-blue/10 rounded transition"
                                          title="View/Print"
                                        >
                                          <i className="fa-solid fa-eye mr-1" />
                                          View
                                        </button>
                                        {!isCreatedScope &&
                                          req.liquidation
                                            ?.return_receipt_file_path && (
                                            <button
                                              onClick={() =>
                                                openReceiptPreview(req)
                                              }
                                              className="px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 rounded transition"
                                              title="View return receipt"
                                            >
                                              <i className="fa-solid fa-file-arrow-up mr-1" />
                                              Receipt
                                            </button>
                                          )}
                                        {!isCreatedScope &&
                                          returnOutstanding > 0 && (
                                            <button
                                              onClick={() =>
                                                openReturnModal(req)
                                              }
                                              className="px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-50 rounded transition"
                                              title={
                                                returnPaidAmount > 0
                                                  ? "Update return payment"
                                                  : "Record return receipt"
                                              }
                                            >
                                              <i className="fa-solid fa-rotate-left mr-1" />
                                              {returnPaidAmount > 0
                                                ? "Update"
                                                : "Return"}
                                            </button>
                                          )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                          ) : (
                            <tr>
                              <td
                                colSpan={7}
                                className="px-4 py-8 text-center text-slate-500"
                              >
                                No liquidations found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Cost Analysis Tab */}
                {activeTab === "cost_analyses" && (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-50 border-b border-slate-200">
                            <th className="px-4 py-3 text-left font-semibold text-slate-600">
                              RFP No
                            </th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-600">
                              Consignee
                            </th>
                            <th className="px-4 py-3 text-center font-semibold text-slate-600">
                              Status
                            </th>
                            <th className="px-4 py-3 text-right font-semibold text-slate-600">
                              Billing
                            </th>
                            <th className="px-4 py-3 text-right font-semibold text-slate-600">
                              Collected
                            </th>
                            <th className="px-4 py-3 text-right font-semibold text-slate-600">
                              Actual Cost
                            </th>
                            <th className="px-4 py-3 text-right font-semibold text-slate-600">
                              Gross Profit
                            </th>
                            <th className="px-4 py-3 text-center font-semibold text-slate-600">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {detailData.requests?.filter((r) => r.cost_analysis)
                            .length ? (
                            detailData.requests
                              .filter((r) => r.cost_analysis)
                              .map((req, idx) => (
                                <tr
                                  key={req.cost_analysis.id}
                                  className={`border-b border-slate-100 ${
                                    idx % 2 === 0 ? "" : "bg-slate-50/50"
                                  }`}
                                >
                                  <td className="px-4 py-3 font-medium">
                                    {req.rfp_no || "-"}
                                  </td>
                                  <td className="px-4 py-3">
                                    {req.cost_analysis_report?.consignee || "-"}
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    {getStatusBadge(req.cost_analysis.status)}
                                  </td>
                                  <td className="px-4 py-3 text-right font-medium">
                                    {formatCurrency(
                                      req.cost_analysis_report
                                        ?.billing_charges_subtotal
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-right">
                                    {formatCurrency(
                                      req.cost_analysis_report
                                        ?.receipted_charges_subtotal
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-right">
                                    {formatCurrency(
                                      req.cost_analysis_report
                                        ?.actual_cost_total
                                    )}
                                  </td>
                                  <td
                                    className={`px-4 py-3 text-right font-medium ${
                                      Number(
                                        req.cost_analysis_report
                                          ?.gross_profit || 0
                                      ) >= 0
                                        ? "text-green-600"
                                        : "text-red"
                                    }`}
                                  >
                                    {formatCurrency(
                                      req.cost_analysis_report?.gross_profit
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    <button
                                      onClick={() => viewCostAnalysisPrint(req)}
                                      className="px-2 py-1 text-xs font-medium text-blue hover:bg-blue/10 rounded transition"
                                      title="View/Print"
                                    >
                                      <i className="fa-solid fa-eye mr-1" />
                                      View
                                    </button>
                                  </td>
                                </tr>
                              ))
                          ) : (
                            <tr>
                              <td
                                colSpan={8}
                                className="px-4 py-8 text-center text-slate-500"
                              >
                                No cost analyses found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Vouchers Tab */}
                {activeTab === "vouchers" && (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-50 border-b border-slate-200">
                            <th className="px-4 py-3 text-left font-semibold text-slate-600">
                              Voucher No
                            </th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-600">
                              Check No
                            </th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-600">
                              Check Date
                            </th>
                            <th className="px-4 py-3 text-right font-semibold text-slate-600">
                              Amount
                            </th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-600">
                              Prepared By
                            </th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-600">
                              Checked By
                            </th>
                            <th className="px-4 py-3 text-center font-semibold text-slate-600">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {detailData.check_vouchers?.length ? (
                            detailData.check_vouchers.map((voucher, idx) => (
                              <tr
                                key={voucher.id}
                                className={`border-b border-slate-100 ${
                                  idx % 2 === 0 ? "" : "bg-slate-50/50"
                                }`}
                              >
                                <td className="px-4 py-3 font-medium">
                                  {voucher.voucher_no || "-"}
                                </td>
                                <td className="px-4 py-3">
                                  {voucher.check_no || "-"}
                                </td>
                                <td className="px-4 py-3">
                                  {formatDate(voucher.check_date)}
                                </td>
                                <td className="px-4 py-3 text-right font-medium">
                                  {formatCurrency(voucher.amount)}
                                </td>
                                <td className="px-4 py-3">
                                  {voucher.preparer_name || "-"}
                                </td>
                                <td className="px-4 py-3">
                                  {voucher.checked_by_name || "-"}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <button
                                    onClick={() => viewVoucherPrint(voucher)}
                                    className="px-2 py-1 text-xs font-medium text-blue hover:bg-blue/10 rounded transition"
                                    title="View/Print"
                                  >
                                    <i className="fa-solid fa-eye mr-1" />
                                    View
                                  </button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td
                                colSpan={7}
                                className="px-4 py-8 text-center text-slate-500"
                              >
                                No vouchers found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            ) : null}
          </div>
        </div>
      </div>,
      document.body
    );
  };

  const PrintPreviewModal = () => {
    if (!printPreview.open) return null;

    return ReactDOM.createPortal(
      <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-950/70 px-4 py-6">
        <div className="flex h-[92vh] w-full max-w-7xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
          <div className="flex items-start justify-between gap-4 border-b border-slate-100 bg-slate-50 px-6 py-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Print Preview
              </p>
              <h3 className="mt-1 text-xl font-bold text-slate-900">
                {printPreview.title || "Document"}
              </h3>
              <p className="text-sm text-slate-500">Ready for printing</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="inline-flex items-center overflow-hidden rounded-xl border border-slate-300 bg-white">
                <button
                  type="button"
                  onClick={() => setPrintZoom((value) => Math.max(0.8, Number((value - 0.1).toFixed(1))))}
                  className="px-3 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-100"
                  title="Zoom out"
                  aria-label="Zoom out"
                >
                  −
                </button>
                <span className="min-w-[58px] border-x border-slate-200 px-2 py-2 text-center text-xs font-semibold text-slate-600">
                  {Math.round(printZoom * 100)}%
                </span>
                <button
                  type="button"
                  onClick={() => setPrintZoom((value) => Math.min(2, Number((value + 0.1).toFixed(1))))}
                  className="px-3 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-100"
                  title="Zoom in"
                  aria-label="Zoom in"
                >
                  +
                </button>
              </div>
              <button
                type="button"
                onClick={printPreviewNow}
                className="inline-flex items-center gap-2 rounded-xl bg-blue px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue/90"
              >
                <i className="fa-solid fa-print" />
                Print
              </button>
              <button
                type="button"
                onClick={closePrintPreview}
                className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
              >
                Close
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-auto bg-slate-100 p-4">
            <div
              className="mx-auto h-full w-full max-w-6xl overflow-visible rounded-2xl bg-white shadow-xl"
              style={{ zoom: printZoom }}
            >
              <iframe
                ref={printFrameRef}
                title={printPreview.title || "Print Preview"}
                srcDoc={printPreview.html}
                className="block h-full w-full overflow-auto"
                onLoad={fitPrintFrameHeight}
                style={{
                  height: "calc(92vh - 120px)",
                  minHeight: 0,
                  maxHeight: "calc(92vh - 120px)",
                  border: 0,
                }}
              />
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  const ReturnActionModal = () => {
    if (!returnModal.open) return null;

    return ReactDOM.createPortal(
      <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-slate-950/70 px-4 py-6">
        <div className="w-full max-w-2xl overflow-hidden rounded-3xl bg-white shadow-2xl">
          <div className="flex items-start justify-between gap-4 border-b border-slate-100 bg-slate-50 px-6 py-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Return Action
              </p>
              <h3 className="mt-1 text-xl font-bold text-slate-900">
                {returnModal.request?.rfp_no || "Liquidation Return"}
              </h3>
              <p className="text-sm text-slate-500">
                Enter the return amount and upload the return receipt before
                proceeding.
              </p>
            </div>
            <button
              type="button"
              onClick={closeReturnModal}
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
            >
              Close
            </button>
          </div>

          <div className="space-y-4 p-6">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              <div className="text-xs uppercase tracking-[0.12em] text-slate-500">
                Job Order
              </div>
              <div className="mt-1 text-sm font-semibold text-slate-900">
                {returnModal.request?.job_order || "—"}
              </div>
              <div className="mt-1 text-xs text-slate-500">
                Return subtotal from liquidation:{" "}
                {formatCurrency(
                  getLiquidationReturnOutstandingAmount(returnModal.request)
                )}
              </div>
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                Return Amount
              </label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={returnModal.amount}
                onChange={(e) =>
                  setReturnModal((current) => ({
                    ...current,
                    amount: e.target.value,
                  }))
                }
                className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-blue focus:ring-2 focus:ring-blue/10"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                Return Receipt
              </label>
              <input
                ref={returnFileInputRef}
                type="file"
                accept=".pdf,image/png,image/jpeg,image/jpg"
                onChange={(e) =>
                  handleReturnFileChange(e.target.files?.[0] || null)
                }
                className="sr-only"
              />
              <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3">
                <button
                  type="button"
                  onClick={triggerReturnFilePicker}
                  className="inline-flex items-center gap-2 rounded-xl bg-blue px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue/90"
                >
                  <i className="fa-solid fa-paperclip" />
                  Choose File
                </button>
                <div className="min-w-0 flex-1 text-sm text-slate-600">
                  {returnModal.file ? (
                    <span className="break-all font-medium text-slate-900">
                      {returnModal.file.name}
                    </span>
                  ) : (
                    <span>No file selected</span>
                  )}
                </div>
              </div>
              <p className="mt-2 text-xs text-slate-500">
                Upload the proof or receipt of the returned amount.
              </p>
            </div>

            {detailError && (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {detailError}
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={closeReturnModal}
                className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitReturnAction}
                disabled={returnLoading}
                className="inline-flex items-center gap-2 rounded-xl bg-green-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {returnLoading ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-rotate-left" />
                    Proceed
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  const ReceiptPreviewModal = () => {
    if (!receiptPreview.open || !receiptPreview.request) return null;

    const request = receiptPreview.request;
    const fileUrl = request.liquidation?.return_receipt_file_path || "";
    const returnAmount = Number(request.liquidation?.return_amount || 0);
    const isPdf = fileUrl.toLowerCase().includes(".pdf");

    return ReactDOM.createPortal(
      <div className="fixed inset-0 z-[10002] flex items-center justify-center bg-slate-950/70 px-4 py-6">
        <div className="flex h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 border-b border-slate-100 bg-slate-50 px-6 py-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Return Receipt
              </p>
              <h3 className="mt-1 text-xl font-bold text-slate-900">
                {request.rfp_no || "Receipt Preview"}
              </h3>
              <p className="text-sm text-slate-500">
                Job Order: {request.job_order || "-"}
              </p>
            </div>
            <button
              type="button"
              onClick={closeReceiptPreview}
              className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
            >
              <i className="fa-solid fa-xmark text-lg" />
            </button>
          </div>

          {/* Return Amount Summary */}
          <div className="border-b border-slate-100 bg-green-50 px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-green-700">
                  Amount Returned
                </p>
                <p className="mt-1 text-2xl font-bold text-green-600">
                  {formatCurrency(returnAmount)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-500">Recorded by</p>
                <p className="text-sm font-medium text-slate-700">
                  {request.liquidation?.return_recorded_by_name || "-"}
                </p>
                <p className="text-xs text-slate-500">
                  {request.liquidation?.return_recorded_at
                    ? formatDate(request.liquidation.return_recorded_at)
                    : "-"}
                </p>
              </div>
            </div>
          </div>

          {/* Document Preview */}
          <div className="flex-1 overflow-auto bg-slate-100 p-4">
            <div className="mx-auto h-full w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-xl">
              {isPdf ? (
                <iframe
                  src={fileUrl}
                  title="Return Receipt PDF"
                  className="block h-full w-full"
                  style={{ minHeight: "calc(92vh - 220px)", border: 0 }}
                />
              ) : (
                <div className="flex h-full items-center justify-center p-4">
                  <img
                    src={fileUrl}
                    alt="Return Receipt"
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-4 border-t border-slate-100 bg-slate-50 px-6 py-4">
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-medium text-blue hover:underline"
            >
              <i className="fa-solid fa-external-link" />
              Open in new tab
            </a>
            <button
              type="button"
              onClick={closeReceiptPreview}
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
            >
              Close
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div className="p-6 font-poppins">
      {/* Header */}
      <div className="mb-6">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
          {portalLabel}
        </p>
        <h1 className="mb-2 text-2xl font-bold text-blue">{pageTitle}</h1>
        <p className="text-sm text-grey">{pageDescription}</p>
      </div>

      {/* Summary Cards */}
      {displaySummary && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4 mb-6">
          <button
            type="button"
            onClick={() => navigate(ledgerBasePath)}
            className="text-left bg-white rounded-2xl p-4 shadow-sm border border-gray-100 min-h-[96px] flex flex-col gap-1 transition hover:shadow-md"
          >
            <p className="text-xs text-grey mb-1">Total JOs</p>
            <p className="text-xl font-bold text-blue leading-tight">
              {displaySummary.totalJos || 0}
            </p>
          </button>
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 min-h-[96px] flex flex-col gap-1">
            <p className="text-xs text-grey mb-1">Total Requested</p>
            <p className="text-xl font-bold text-blue leading-tight">
              {formatCurrency(displaySummary.totalRequested)}
            </p>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 min-h-[96px] flex flex-col gap-1">
            <p className="text-xs text-grey mb-1">Total Liquidated</p>
            <p className="text-xl font-bold text-indigo-600 leading-tight">
              {formatCurrency(displaySummary.totalLiquidated)}
            </p>
          </div>
          {!isCreatedScope && !hideBilled && (
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 min-h-[96px] flex flex-col gap-1">
              <p className="text-xs text-grey mb-1">Total Billed</p>
              <p className="text-xl font-bold text-blue leading-tight">
                {formatCurrency(displaySummary.totalBilled)}
              </p>
            </div>
          )}
          <div
            className={`rounded-2xl p-4 shadow-sm border min-h-[96px] flex flex-col gap-1 ${
              displayReimbursementTotal > 0
                ? "bg-amber-50 border-amber-200"
                : "bg-white border-gray-100"
            }`}
          >
            <p className="text-xs text-grey mb-1">Reimbursement</p>
            <p
              className={`text-xl font-bold leading-tight ${
                displayReimbursementTotal > 0
                  ? "text-amber-600"
                  : "text-slate-400"
              }`}
            >
              {formatCurrency(displayReimbursementTotal)}
            </p>
            {displayReimbursementTotal > 0 && (
              <p className="text-xs text-amber-600 italic">
                Subject for new request
              </p>
            )}
          </div>
          <div
            className={`rounded-2xl p-4 shadow-sm border min-h-[96px] flex flex-col gap-1 ${
              displayReturnTotal > 0
                ? "bg-green-50 border-green-200"
                : "bg-white border-gray-100"
            }`}
          >
            <p className="text-xs text-grey mb-1">Return</p>
            <p
              className={`text-xl font-bold leading-tight ${
                displayReturnTotal > 0 ? "text-green-600" : "text-slate-400"
              }`}
            >
              {formatCurrency(displayReturnTotal)}
            </p>
            {displayReturnTotal > 0 && (
              <p className="text-xs text-green-600 italic">
                {detailData?.requests?.some(
                  (req) => getLiquidationReturnStatus(req).label === "Partial"
                )
                  ? "Partial return"
                  : detailData?.requests?.some(
                      (req) =>
                        getLiquidationReturnStatus(req).label === "Settled"
                    )
                  ? "Settled via return receipt"
                  : "Subject for return"}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => navigate(`${ledgerBasePath}/unliquidated`)}
            className={`text-left rounded-2xl p-4 shadow-sm border min-h-[96px] flex flex-col gap-1 transition hover:shadow-md ${
              isUnliquidatedRoute
                ? "bg-amber-50 border-amber-200"
                : "bg-white border-gray-100"
            }`}
          >
            <p className="text-xs text-grey mb-1">Unliquidated</p>
            <p
              className={`text-xl font-bold leading-tight ${
                displayUnliquidatedTotal > 0
                  ? "text-amber-600"
                  : "text-slate-400"
              }`}
            >
              {formatCurrency(displayUnliquidatedTotal)}
            </p>
            {displayUnliquidatedTotal > 0 && (
              <p className="text-xs text-amber-600 italic">
                Open JOs with unliquidated rows
              </p>
            )}
          </button>
          {!isCreatedScope && !hideGrossProfit && (
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 min-h-[96px] flex flex-col gap-1">
              <p className="text-xs text-grey mb-1">Gross Profit</p>
              <p
                className={`text-xl font-bold leading-tight ${
                  displaySummary.totalGrossProfit >= 0
                    ? "text-green-600"
                    : "text-red"
                }`}
              >
                {formatCurrency(displaySummary.totalGrossProfit)}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 mb-6">
        <div
          className={`grid grid-cols-1 gap-4 lg:items-end ${
            isRequestorPortal
              ? "lg:grid-cols-[160px_minmax(0,1fr)_minmax(0,1fr)_160px_auto]"
              : "lg:grid-cols-[140px_minmax(0,1fr)_minmax(0,1fr)_140px_140px_auto]"
          }`}
        >
          {/* Date Filter */}
          <div>
            <label className="block text-xs text-grey mb-1">Date Filter</label>
            <select
              value={dateFilter}
              onChange={(e) => {
                const nextValue = e.target.value;
                if (nextValue === "custom") {
                  previousDateFilterRef.current = dateFilter;
                  setDateFilter(previousDateFilterRef.current || "all");
                  openCustomDateModal();
                  return;
                }
                setDateFilter(nextValue);
              }}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue/20 focus:border-blue outline-none"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {/* Job Order Filter */}
          <div>
            <label className="block text-xs text-grey mb-1">Job Order</label>
            <input
              type="text"
              value={jobOrderFilter}
              onChange={(e) => setJobOrderFilter(e.target.value)}
              placeholder="Search JO..."
              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue/20 focus:border-blue outline-none"
            />
          </div>

          {/* Client Name Filter */}
          <div>
            <label className="block text-xs text-grey mb-1">Client Name</label>
            <input
              type="text"
              value={clientNameFilter}
              onChange={(e) => setClientNameFilter(e.target.value)}
              placeholder="Search client..."
              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue/20 focus:border-blue outline-none"
            />
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-xs text-grey mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue/20 focus:border-blue outline-none"
            >
              <option value="all">All</option>
              <option value="has_floating">Has Floating</option>
              <option value="fully_liquidated">Fully Liquidated</option>
              <option value="pending">Pending Requests</option>
            </select>
          </div>

          {/* Scope Filter - Only for accounting/superadmin portals */}
          {!isRequestorPortal && !isAdminPortal && (
            <div>
              <label className="block text-xs text-grey mb-1">Scope</label>
              <select
                value={scopeFilter}
                onChange={(e) => setScopeFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue/20 focus:border-blue outline-none"
              >
                <option value="all">All Requests</option>
                <option value="created">My Requests Only</option>
              </select>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-end gap-3 lg:justify-self-end">
            <button
              onClick={handleSearch}
              disabled={loading}
              className="inline-flex min-w-[100px] items-center justify-center gap-2 px-4 py-2 bg-blue text-white rounded-xl text-sm font-semibold hover:bg-blue/90 transition-colors disabled:opacity-50 shadow-sm"
            >
              <i className="fa-solid fa-search"></i>
              Search
            </button>
            <button
              onClick={handleReset}
              className="inline-flex min-w-[80px] items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-grey rounded-xl text-sm font-semibold hover:bg-gray-200 transition-colors shadow-sm"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {customDateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
            <div className="mb-4">
              <h2 className="text-lg font-bold text-blue">Custom Date Range</h2>
              <p className="text-sm text-grey">
                Choose the start and end date for the JO ledger.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-xs text-grey mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={customDateDraftStart}
                  onChange={(e) => setCustomDateDraftStart(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue/20 focus:border-blue outline-none"
                />
              </div>
              <div>
                <label className="block text-xs text-grey mb-1">End Date</label>
                <input
                  type="date"
                  value={customDateDraftEnd}
                  onChange={(e) => setCustomDateDraftEnd(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue/20 focus:border-blue outline-none"
                />
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-3">
              <button
                onClick={closeCustomDateModal}
                className="inline-flex items-center justify-center rounded-xl bg-gray-100 px-4 py-2 text-sm font-semibold text-grey transition hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={applyCustomDateRange}
                disabled={!customDateDraftStart || !customDateDraftEnd}
                className="inline-flex items-center justify-center rounded-xl bg-blue px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue/90 disabled:opacity-50"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-4 text-sm text-grey">
        <span className="font-semibold text-slate-600">Active Date Range:</span>{" "}
        <span className="text-blue">{getActiveDateRangeLabel()}</span>
      </div>

      {/* Export Button */}
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-grey">
          {isUnliquidatedRoute
            ? `Showing ${filteredData.length} Job Order${
                filteredData.length !== 1 ? "s" : ""
              } with unliquidated amounts`
            : `Showing ${filteredData.length} Job Order${
                filteredData.length !== 1 ? "s" : ""
              }`}
        </p>
        <button
          onClick={exportToExcel}
          disabled={filteredData.length === 0}
          className="px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700 transition-colors disabled:opacity-50"
        >
          <i className="fa-solid fa-file-excel mr-2"></i>
          Export to Excel
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red/10 border border-red/20 rounded-xl p-4 mb-4">
          <p className="text-red text-sm">{error}</p>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-blue text-white">
                <th
                  className="px-4 py-3 text-left font-semibold whitespace-nowrap cursor-pointer hover:bg-blue/80"
                  onClick={() => handleSort("job_order")}
                >
                  <div className="flex items-center gap-2">
                    <span>JO Number</span>
                    <i
                      className={`fa-solid ${getSortIcon(
                        "job_order"
                      )} text-xs opacity-70`}
                    ></i>
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left font-semibold whitespace-nowrap cursor-pointer hover:bg-blue/80"
                  onClick={() => handleSort("client_name")}
                >
                  <div className="flex items-center gap-2">
                    <span>Client</span>
                    <i
                      className={`fa-solid ${getSortIcon(
                        "client_name"
                      )} text-xs opacity-70`}
                    ></i>
                  </div>
                </th>
                <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">
                  Req
                </th>
                <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">
                  Liq
                </th>
                {showCostAnalysis && (
                  <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">
                    CA
                  </th>
                )}
                <th
                  className="px-4 py-3 text-right font-semibold whitespace-nowrap cursor-pointer hover:bg-blue/80"
                  onClick={() => handleSort("summary.total_requested")}
                >
                  <div className="flex items-center justify-end gap-2">
                    <span>Requested</span>
                    <i
                      className={`fa-solid ${getSortIcon(
                        "summary.total_requested"
                      )} text-xs opacity-70`}
                    ></i>
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-right font-semibold whitespace-nowrap cursor-pointer hover:bg-blue/80"
                  onClick={() => handleSort("summary.total_liquidated")}
                >
                  <div className="flex items-center justify-end gap-2">
                    <span>Liquidated</span>
                    <i
                      className={`fa-solid ${getSortIcon(
                        "summary.total_liquidated"
                      )} text-xs opacity-70`}
                    ></i>
                  </div>
                </th>
                {showBilled && (
                  <th
                    className="px-4 py-3 text-right font-semibold whitespace-nowrap cursor-pointer hover:bg-blue/80"
                    onClick={() => handleSort("summary.total_billed")}
                  >
                    <div className="flex items-center justify-end gap-2">
                      <span>Billed</span>
                      <i
                        className={`fa-solid ${getSortIcon(
                          "summary.total_billed"
                        )} text-xs opacity-70`}
                      ></i>
                    </div>
                  </th>
                )}
                <th
                  className="px-4 py-3 text-right font-semibold whitespace-nowrap cursor-pointer hover:bg-blue/80"
                  onClick={() => handleSort("summary.total_reimbursement")}
                >
                  <div className="flex items-center justify-end gap-2">
                    <span>Reimburse</span>
                    <i
                      className={`fa-solid ${getSortIcon(
                        "summary.total_reimbursement"
                      )} text-xs opacity-70`}
                    ></i>
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-right font-semibold whitespace-nowrap cursor-pointer hover:bg-blue/80"
                  onClick={() => handleSort("summary.total_return")}
                >
                  <div className="flex items-center justify-end gap-2">
                    <span>Return</span>
                    <i
                      className={`fa-solid ${getSortIcon(
                        "summary.total_return"
                      )} text-xs opacity-70`}
                    ></i>
                  </div>
                </th>
                {showGrossProfit && (
                  <th
                    className="px-4 py-3 text-right font-semibold whitespace-nowrap cursor-pointer hover:bg-blue/80"
                    onClick={() => handleSort("summary.gross_profit")}
                  >
                    <div className="flex items-center justify-end gap-2">
                      <span>Profit</span>
                      <i
                        className={`fa-solid ${getSortIcon(
                          "summary.gross_profit"
                        )} text-xs opacity-70`}
                      ></i>
                    </div>
                  </th>
                )}
                <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={mainTableColumnSpan}
                    className="px-4 py-12 text-center"
                  >
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-8 h-8 border-3 border-blue/20 border-t-blue rounded-full animate-spin"></div>
                      <p className="text-grey">Loading JO ledger data...</p>
                    </div>
                  </td>
                </tr>
              ) : filteredData.length === 0 ? (
                <tr>
                  <td
                    colSpan={mainTableColumnSpan}
                    className="px-4 py-12 text-center"
                  >
                    <div className="flex flex-col items-center gap-3">
                      <i className="fa-solid fa-folder-open text-4xl text-gray-300"></i>
                      <p className="text-grey">No JO ledger data found</p>
                      <p className="text-xs text-gray-400">
                        Try adjusting your filters or check if requests with job
                        orders exist
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredData.map((row, idx) => (
                  <tr
                    key={row.job_order}
                    className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                      idx % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                    }`}
                  >
                    <td className="px-4 py-3 font-medium text-blue">
                      {row.job_order}
                    </td>
                    <td className="px-4 py-3">{row.client_name || "-"}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex px-2 py-1 bg-blue-50 text-blue rounded-lg text-xs font-medium">
                        {row.summary?.request_count || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex px-2 py-1 bg-indigo-50 text-indigo-600 rounded-lg text-xs font-medium">
                        {row.summary?.liquidation_count || 0}
                      </span>
                    </td>
                    {showCostAnalysis && (
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex px-2 py-1 bg-purple-50 text-purple-600 rounded-lg text-xs font-medium">
                          {row.summary?.cost_analysis_count || 0}
                        </span>
                      </td>
                    )}
                    <td className="px-4 py-3 text-right font-medium">
                      {formatCurrency(row.summary?.total_requested)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {formatCurrency(row.summary?.total_liquidated)}
                    </td>
                    {showBilled && (
                      <td className="px-4 py-3 text-right">
                        {formatCurrency(row.summary?.total_billed)}
                      </td>
                    )}
                    <td className="px-4 py-3 text-right">
                      {getRowReimbursementTotal(row) > 0 ? (
                        <span className="text-amber-600 font-medium">
                          {formatCurrency(getRowReimbursementTotal(row))}
                        </span>
                      ) : (
                        <span className="text-slate-400">
                          {formatCurrency(0)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {getRowReturnOutstandingTotal(row) > 0 ? (
                        <span className="text-green-600 font-medium">
                          {formatCurrency(getRowReturnOutstandingTotal(row))}
                        </span>
                      ) : (
                        <span className="text-slate-400">
                          {formatCurrency(0)}
                        </span>
                      )}
                    </td>
                    {showGrossProfit && (
                      <td
                        className={`px-4 py-3 text-right font-medium ${
                          row.summary?.gross_profit >= 0
                            ? "text-green-600"
                            : "text-red"
                        }`}
                      >
                        {formatCurrency(row.summary?.gross_profit)}
                      </td>
                    )}
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => openJoDetail(row.job_order)}
                        className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white transition hover:bg-slate-800"
                      >
                        <i className="fa-solid fa-eye" />
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Modal */}
      <DetailModal />

      {/* Print Preview Modal */}
      <>
        <PrintPreviewModal />
        <ReturnActionModal />
        <ReceiptPreviewModal />
      </>
    </div>
  );
};

export default JoLedger;
